/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import type { FC, ReactNode, SVGProps } from 'react';

import { Notification } from '../../ui-library/Elements/Notification';
import EllipseEffect from '../EllipseEffect';

interface Props {
  children: ReactNode;
  href: string;
  icon?: FC<SVGProps<SVGElement>>;
  active?: boolean;
  notification?: boolean;
  onClick?: () => void;
}

const ActiveComponent = () => {
  return (
    <motion.div
      initial={{ opacity: 0, width: '0%' }}
      animate={{ opacity: 1, width: '100%' }}
      exit={{ opacity: 0, width: '0%' }}
      transition={{ duration: 0.3 }}
      className='bottom-0 h-1 rounded-t-xs bg-button-surface-primary shadow-[0_0_8px_0_rgba(204,0,0,0.6)] lg:absolute'
    />
  );
};

export const Tab = ({ children, href, icon: Icon, active, notification, onClick }: Props) => {
  return (
    <div
      className={classNames(
        'relative',
        'inline-flex flex-col items-center lg:justify-center',
        'max-w-40 min-w-fit flex-1 lg:max-w-full',
        'px-4 lg:px-0',
        'overflow-visible',
      )}
    >
      <Link
        href={href}
        className={classNames(
          'lg:px-2',
          'inline-flex shrink-0 flex-col items-center justify-center',
          'h-[calc(100%-0.25rem)] lg:h-auto',
          'rounded-sm',
          'font-sans text-xs font-semibold capitalize sm:text-sm lg:text-lg',
          'transition-all duration-300',
          'shadow-none outline-offset-0',
          {
            'pointer-events-none text-neutral-text-light lg:text-neutral-text-xlight': active,
            'text-neutral-text-medium lg:text-neutral-text-light': !active,
            'hover:text-neutral-text-xlight hover:lg:bg-button-plain/40 hover:lg:text-neutral-text-light': !active,
            'focus-visible:bg-button-plain/40': !active,
            'focus-visible:outline-1': !active,
          },
        )}
        role='menuitem'
        aria-current={active && 'page'}
        tabIndex={active ? -1 : 0}
        onClick={onClick}
        scroll={false}
        data-cy={`${children as string}-tab`}
      >
        <span className='flex shrink-0 flex-col items-center'>
          {Icon && (
            <EllipseEffect variant={active ? 'selected' : 'hidden'}>
              <Icon className='relative z-1 size-6' />
            </EllipseEffect>
          )}
          {children}
        </span>
        {notification && <Notification />}
      </Link>
      <AnimatePresence>{active && <ActiveComponent />}</AnimatePresence>
    </div>
  );
};
